import { Component, computed, EventEmitter, inject, input, OnInit, Output, signal } from '@angular/core';
import { InspectionGuidelinesDetailDto, InspectionGuidelinesService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-inspection-guidelines-window',
  templateUrl: './inspection-guidelines-window.component.html',
  styleUrls: ['./inspection-guidelines-window.component.scss'],
})
export class InspectionGuidelinesWindowComponent implements OnInit {
  private readonly inspectionGuidelinesService = inject(InspectionGuidelinesService);

  readonly inspectionId = input.required<string>();
  readonly type = input.required<'CLERK' | 'TECHNICIAN'>();
  @Output() saved = new EventEmitter<InspectionGuidelinesDetailDto>();

  readonly guidelines = signal<InspectionGuidelinesDetailDto | null>(null);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly minimized = signal(true);

  private readonly _checkedIndices = signal<Set<number>>(new Set());

  readonly title = computed(() => {
    const dto = this.guidelines();
    const label = this.type() === 'CLERK' ? 'Sachbearbeiter-Leitfaden' : 'Techniker-Leitfaden';
    if (dto && dto.checklistItems.length > 0) {
      return `${label} (${this._checkedIndices().size}/${dto.checklistItems.length})`;
    }
    return label;
  });

  async ngOnInit(): Promise<void> {
    const dto = await firstValueFrom(
      this.inspectionGuidelinesService.findByInspectionAndType(this.inspectionId(), this.type()),
    );
    this.guidelines.set(dto ?? null);
    this._checkedIndices.set(this.toCheckedIndices(dto?.checklistItems ?? [], dto?.checkedItems ?? []));
    this.loading.set(false);
  }

  private toCheckedIndices(checklistItems: string[], checkedItems: string[]): Set<number> {
    const indices = new Set<number>();
    const remaining = [...checkedItems];
    for (let i = 0; i < checklistItems.length; i++) {
      const pos = remaining.indexOf(checklistItems[i]);
      if (pos !== -1) {
        indices.add(i);
        remaining.splice(pos, 1);
      }
    }
    return indices;
  }

  toggleMinimized(): void {
    this.minimized.update(v => !v);
  }

  isChecked(index: number): boolean {
    return this._checkedIndices().has(index);
  }

  async toggleItem(index: number): Promise<void> {
    const next = new Set(this._checkedIndices());
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    this._checkedIndices.set(next);
    await this.save();
  }

  async save(): Promise<void> {
    const dto = this.guidelines();
    if (!dto) return;
    this.saving.set(true);
    try {
      const checkedItems = Array.from(this._checkedIndices()).map(i => dto.checklistItems[i]);
      const updated = await firstValueFrom(
        this.inspectionGuidelinesService.updateCheckedItems(
          this.inspectionId(),
          this.type(),
          { checkedItems },
        ),
      );
      this.saved.emit(updated);
    } finally {
      this.saving.set(false);
    }
  }
}
